/**
 * ══ one name per page, read from the route table ═══════════════════════════════════════════════════
 *
 * §14.5 N6: `meta.title` is a page's name — its `<h1>`, its browser tab, the header's location line and
 * the finder's index entry — and the sidebar renders `meta.navLabel ?? meta.title`. This module is the
 * one place that resolution happens, so the menu, the header and the finder cannot disagree about what
 * a page is called.
 *
 * Before this, the sidebar typed its own literal for every row. Twenty-two pages therefore answered to
 * two names, and four of those named a DIFFERENT SUBJECT: an operator clicked «مرکز فرماندهی», landed
 * on a page whose heading read «داشبورد مدیریتی», and had a browser tab agreeing with neither the row
 * they clicked nor the row that was highlighted. `systemSweep`'s R13.nav counted 22; there is one
 * string per page now, so the count is structurally zero rather than lowered.
 *
 * Read off `router.options.routes` rather than `router.getRoutes()` so this works identically in a spec
 * that constructs its own router from the same table, and so the param routes keep their raw `:id`
 * paths rather than the compiled matcher's.
 */
import router, { landingRouteName } from '@/router'

const table = () => router.options.routes

/** `path -> route`, built once. The table is a module-level constant; it does not change at runtime. */
const BY_PATH = new Map(table().map((route) => [route.path, route]))

/** `name -> path`, for turning `landingRouteName()` into something the menu can compare against. */
const BY_NAME = new Map(table().map((route) => [route.name, route.path]))

/** The page's full name. Falls back to the path so a missing title is visible rather than blank. */
export const titleOf = (path) => BY_PATH.get(path)?.meta?.title ?? path

/**
 * The sidebar's name for a row: the declared shortening, or the page's full name.
 *
 * A `navLabel` is legal only where the accordion above the row already supplies the word being dropped,
 * and `theWayIn.spec.js` enforces the mechanical half — it must share a content word with the title and
 * must not be longer than it. Six routes declare one.
 */
export const navLabelOf = (path) => {
  const meta = BY_PATH.get(path)?.meta

  return meta?.navLabel ?? meta?.title ?? path
}

/**
 * Where this operator's session lands, as a PATH.
 *
 * The sidebar's board band is ordered so this row is first, which is what turns «the screen opened
 * before any other» into a fact rather than a comment: the router's answer and the menu's first row are
 * read from the same function.
 */
export const landingPath = () => BY_NAME.get(landingRouteName()) ?? null

/**
 * Every page the finder may offer, before permission filtering.
 *
 * Detail routes (`:id`) are excluded — they cannot be reached without a record — and so are the public
 * auth and 404 routes, for the same reasons `AppSidebar.spec.js` exempts them from reachability.
 * `permission` is carried through UNRESOLVED so the finder gates each hit with the same `can()` the
 * sidebar and the router's guard use; `useKeyboardShortcuts.js:20-31` records what an ungated directory
 * of this panel cost the last time one shipped, and this one is 117× the size.
 */
export const PAGE_INDEX = table()
  .filter((route) => !route.meta?.public && !route.meta?.hidden && !route.path.includes(':') && !route.redirect)
  .map((route) => ({
    path: route.path,
    name: route.name,
    title: route.meta?.title ?? route.path,
    navLabel: route.meta?.navLabel ?? null,
    permission: route.meta?.permission ?? null,
    feature: route.meta?.feature ?? null,
  }))
